import classnames from 'classnames';
import _ from 'lodash';
import { Popover } from 'antd';
import {
  IconButton,
  IcMore as moreIcon,
  IcArrowLeft as icArrowLeft,
  IcArrowRight as icArrowRight,
} from '@/shared/ui';
import { TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { isValidHr } from '@/entities/heart-rate';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import MenuHrChart from './menuHrChart';
import useEventHrInfo from './useEventHrInfo';

export type EventHrInfoProps = {
  eventIndexes: any[];
  events: any[];
  isActive?: boolean;
  aiChannel?: any;
  setSelectedEventIndex: (index: any) => void;
  defaultIndex: number;
  callbackSetEventIndex: (index: any) => void;
  timezoneOffset?: number;
};

/**
 * Renders the selected-event detail card in the HR summary tab, showing timestamp, channel, heart-rate figures and duration. Reads the current event and navigation callbacks from useEventHrInfo, colours the card by event type, offers left and right arrows when several events share the column, prints avg-only heart rate for AFib while hiding HR for artifact, pause and beat events, and adds a more-actions popover holding MenuHrChart.
 *
 * @param props - The grouped events, their indexes, the active flag and the timezone offset.
 * @returns The event detail card with its info items, optional navigation arrows and actions popover.
 */
const EventHrInfo = ({
  eventIndexes,
  events,
  isActive,
  aiChannel,
  setSelectedEventIndex,
  defaultIndex,
  callbackSetEventIndex,
  timezoneOffset,
}: EventHrInfoProps) => {
  const {
    isOpenPopover,
    eventIndex,
    event,
    eventType,
    channel,
    isBeatEvent,
    handleOpenChange,
    hidePopover,
    onClickLeft,
    onClickRight,
  } = useEventHrInfo({
    eventIndexes,
    events,
    isActive,
    aiChannel,
    setSelectedEventIndex,
    defaultIndex,
    callbackSetEventIndex,
  });

  return (
    <div
      className={classnames(
        'event-info',
        event.type
          ? event.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ? '--artifact-event-clicked'
            : event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB
              ? '--afib-event-clicked'
              : '--event-clicked'
          : '',
        isActive ? '--active' : '',
      )}
    >
      <div className="event-info-content">
        <div className="left-container">
          <div className="event-info-item">
            {events?.length > 1 && (
              <div className="circle-navigation">
                <button
                  type="button"
                  style={{ border: 'none', background: 'transparent', padding: 0 }}
                  onClick={onClickLeft}
                >
                  <img src={icArrowLeft} alt="icon arrow" />
                </button>
                <div className="text-event-index">{`#${eventIndex + 1}`}</div>
                <button
                  type="button"
                  style={{ border: 'none', background: 'transparent', padding: 0 }}
                  onClick={onClickRight}
                >
                  <img src={icArrowRight} alt="icon arrow" />
                </button>
              </div>
            )}
            {eventType ? (
              <div className="info-item">
                <div className="title">Start time</div>
                <div className="time">
                  {`${convertToMomentJs(event.start, timezoneOffset)?.format('MM/DD/YYYY, HH:mm:ss')}`}
                </div>
              </div>
            ) : (
              <div className="info-item">
                <div className="title">Time</div>
                <div className="time">
                  {`${convertToMomentJs(event.startTimeEpoch, timezoneOffset)?.format('MM/DD/YYYY, HH:mm:ss')}`}
                </div>
              </div>
            )}
            {channel && (
              <div className="info-item">
                <div className="title">Channel</div>
                <div className="time">{`CH${channel}`}</div>
              </div>
            )}
            {isBeatEvent ||
            _.includes(
              [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT, TYPE_RHYTHM_EVENT_ENUM.PAUSE],
              eventType,
            ) ? null : (
              <div className="info-item --multi">
                {eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ? (
                  <div>
                    <div className="title">Avg HR</div>
                    <strong>{isValidHr(event.avgHr) ? `${event.avgHr} bpm` : 'NA'}</strong>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="title">Min HR</div>
                      <strong>{isValidHr(event.minHr) ? `${event.minHr} bpm` : 'NA'}</strong>
                    </div>
                    <div>
                      <div className="title">Avg HR</div>
                      <strong>{isValidHr(event.avgHr) ? `${event.avgHr} bpm` : 'NA'}</strong>
                    </div>
                    <div>
                      <div className="title">Max HR</div>
                      <strong>{isValidHr(event.maxHr) ? `${event.maxHr} bpm` : 'NA'}</strong>
                    </div>
                  </>
                )}
              </div>
            )}
            {eventType && !isBeatEvent && (
              <div className="info-item">
                <div className="title">Duration</div>
                <strong>{event.duration}</strong>
              </div>
            )}
          </div>
        </div>
        {eventType &&
          !_.includes(
            [TYPE_RHYTHM_EVENT_ENUM.MAX_HR, TYPE_RHYTHM_EVENT_ENUM.MIN_HR],
            eventType,
          ) && (
            <div className="right-container">
              <Popover
                trigger="click"
                placement="bottom"
                overlayClassName="custom-ant-popover"
                open={isOpenPopover}
                onOpenChange={handleOpenChange}
                content={
                  <div className="more-options flex-col">
                    <MenuHrChart hidePopover={hidePopover} selectedEvent={event} />
                  </div>
                }
              >
                <IconButton
                  className="more-icon"
                  isOutline
                  iconComponent={<img src={moreIcon} alt="More icon" />}
                  onClick={() => {}}
                />
              </Popover>
            </div>
          )}
      </div>
    </div>
  );
};

export default EventHrInfo;
